import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, PlusCircle, Copy, Trash2, Wallet, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchTenant } from '../../redux/tenantActions';
import { fetchVPAs, deleteVPA } from '../../redux/vpaActions';
import { fetchProgramWithId } from '../../redux/programActions';
import AddVpaDrawer from '../collect/AddVpaDrawer';
import { useCollectionMode } from '../../hooks/useCollectionMode';
import PlaceholderImage from '../../assests/placeholder_card_img.jpg';
import type { VpaRecord } from '../../types/domain';

function ShopDetailScreen() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isWalletAddressCollection } = useCollectionMode();
  const { tenant_details, loading } = useSelector((s: any) => s.tenant);
  const { vpa } = useSelector((s: any) => s.vpa);
  const [showAddVpa, setShowAddVpa] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | number | null>(null);
  const [showInfo, setShowInfo] = useState(
    Boolean((location.state as { openInfo?: boolean } | null)?.openInfo),
  );

  useEffect(() => {
    if (tenantId) {
      // @ts-expect-error thunk type
      dispatch(fetchTenant(tenantId));
      // @ts-expect-error thunk type
      dispatch(fetchVPAs(tenantId));
    }
  }, [dispatch, tenantId]);

  useEffect(() => {
    const pid = tenant_details?.programId;
    if (pid != null) {
      // @ts-expect-error thunk type
      dispatch(fetchProgramWithId(pid));
    }
  }, [dispatch, tenant_details?.programId]);

  const refreshVpas = () => {
    if (tenantId) {
      // @ts-expect-error thunk type
      dispatch(fetchVPAs(tenantId));
    }
  };

  const vpas: VpaRecord[] =
    vpa && Array.isArray(vpa) && vpa[0] && 'vpa' in vpa[0] ? (vpa[0].vpa as VpaRecord[]) : [];

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDelete = async () => {
    if (!tenantId || deleteConfirm == null) return;
    try {
      // @ts-expect-error thunk type
      await dispatch(deleteVPA(tenantId, deleteConfirm));
      setDeleteConfirm(null);
      toast.success(isWalletAddressCollection ? 'Wallet removed' : 'VPA removed');
    } catch {
      toast.error(isWalletAddressCollection ? 'Failed to remove wallet' : 'Failed to delete');
    }
  };

  const onVpaAdded = () => {
    setShowAddVpa(false);
    refreshVpas();
  };

  if (loading && !tenant_details) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenant_details) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center">
        <p className="text-gray-500">Shop not found</p>
        <button
          type="button"
          onClick={() => navigate('/app/shops')}
          className="mt-4 text-brand"
        >
          Back to Shops
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-surface pb-6">
      <header className="fc-header">
        <button
          type="button"
          onClick={() => navigate('/app/shops')}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md hover:bg-slate-100 active:bg-slate-200 -ml-1"
          aria-label="Back to shops"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
            {tenant_details.tenantName}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 truncate">
            {tenant_details.tenantBrand}
          </p>
        </div>
      </header>

      <div className="p-4 pb-6 space-y-5">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowInfo(true)}
            className="text-xs sm:text-sm font-medium text-brand hover:text-brand-dark underline underline-offset-2"
          >
            View shop details
          </button>
        </div>

        <div className="fc-card p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <CreditCard className="w-5 h-5 text-brand" />
              {isWalletAddressCollection ? 'Wallet addresses' : 'VPAs'}
            </h2>
            <button
              type="button"
              onClick={() => setShowAddVpa(true)}
              className="flex items-center justify-center gap-2 min-h-[44px] px-5 fc-btn-primary w-full sm:w-auto"
            >
              <PlusCircle className="w-5 h-5" />
              {isWalletAddressCollection ? 'Add wallet' : 'Add VPA'}
            </button>
          </div>

          {vpas.length === 0 ? (
            <div className="py-8 flex flex-col items-center justify-center gap-3 text-gray-500 border border-dashed border-gray-200 rounded-lg text-sm">
              <Wallet className="w-12 h-12 text-gray-300" />
              <span>{isWalletAddressCollection ? 'No wallet addresses yet.' : 'No VPAs yet.'}</span>
              <span className="text-xs">
                {isWalletAddressCollection ? 'Tap Add wallet to add one.' : 'Tap Add VPA to add one.'}
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {vpas.map((item) => (
                <div
                  key={item.vpaId}
                  className="flex items-center justify-between gap-3 min-h-[60px] p-3 sm:p-4 border border-slate-200 rounded-md bg-white"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-12 h-12 flex-shrink-0">
                      <img
                        src={item.qrImage || PlaceholderImage}
                        alt=""
                        className="w-full h-full object-contain bg-gray-50 rounded-md p-1.5"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate text-sm sm:text-base">
                        {item.vpa}
                      </p>
                      {item.providerName && (
                        <p className="text-xs sm:text-sm text-slate-500 truncate">
                          {item.providerName}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopy(item.vpa ?? '')}
                      className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md hover:bg-gray-100 active:bg-gray-200"
                      aria-label={isWalletAddressCollection ? 'Copy wallet address' : 'Copy VPA'}
                    >
                      <Copy className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(item.vpaId)}
                      className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-md hover:bg-red-50 active:opacity-80"
                      aria-label={isWalletAddressCollection ? 'Delete wallet' : 'Delete VPA'}
                    >
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {deleteConfirm != null && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-soft-lg border border-gray-200 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-gray-900">
              {isWalletAddressCollection ? 'Delete wallet address?' : 'Delete VPA?'}
            </h2>
            <p className="text-xs text-gray-600">
              {isWalletAddressCollection
                ? 'This will remove this wallet address from this shop. You can&apos;t undo this action.'
                : 'This will remove this VPA from this shop. You can&apos;t undo this action.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showInfo && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setShowInfo(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-soft-lg border border-gray-200 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Shop details</h2>
              <button
                type="button"
                onClick={() => setShowInfo(false)}
                className="text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <dl className="grid grid-cols-1 gap-y-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-[0.08em]">
                  Contact person
                </dt>
                <dd className="mt-0.5 text-gray-900">
                  {tenant_details.storeCoordinator || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-[0.08em]">
                  Phone number
                </dt>
                <dd className="mt-0.5 text-gray-900">
                  {tenant_details.phone || '-'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-[0.08em]">
                  Address
                </dt>
                <dd className="mt-0.5 text-gray-900 text-sm leading-snug">
                  {tenant_details.address || '-'}
                </dd>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-[0.08em]">
                    Wing
                  </dt>
                  <dd className="mt-0.5 text-gray-900">
                    {tenant_details.wing || '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-[0.08em]">
                    Floor
                  </dt>
                  <dd className="mt-0.5 text-gray-900">
                    {tenant_details.floor || '-'}
                  </dd>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-[0.08em]">
                    State
                  </dt>
                  <dd className="mt-0.5 text-gray-900">
                    {tenant_details.state || '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-[0.08em]">
                    City
                  </dt>
                  <dd className="mt-0.5 text-gray-900">
                    {tenant_details.city || '-'}
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>
      )}

      <AddVpaDrawer
        open={showAddVpa}
        onOpenChange={setShowAddVpa}
        tenantId={tenantId ?? ''}
        tenantName={tenant_details.tenantName}
        onSuccess={onVpaAdded}
      />
    </div>
  );
}

export default ShopDetailScreen;
